import { useCallback, useEffect, useState } from 'react';
import { UnauthedHubSpotUserAvatar } from 'avatar-components/UnauthedHubSpotUserAvatar';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import { List } from 'foundations-components/transitional/components/List';
import { Paginator } from 'foundations-components/transitional/components/Paginator';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import { formatUserFullNameForDisplay } from '../utils/UserUtils';
import usePaginatedUsers from '../hooks/usePaginatedUsers';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const UserCheckbox = ({
  id,
  email,
  displayName,
  checked,
  isMemberOfSelectedTeam,
  onAddUser,
  onRemoveUser,
  disabled,
  disallowedUserTooltip
}) => {
  const checkbox = /*#__PURE__*/_jsx(Checkbox, {
    value: id,
    checked: checked,
    disabled: isMemberOfSelectedTeam || disabled,
    alignment: "center",
    paddingInlineEnd: "100",
    "data-test-id": "user-checkbox",
    onChange: () => {
      if (checked) {
        onRemoveUser(id);
      } else {
        onAddUser(id);
      }
    },
    children: /*#__PURE__*/_jsxs(Flex, {
      alignItems: "center",
      "data-test-id": `assign-user-checkbox-${id}`,
      children: [/*#__PURE__*/_jsx(UnauthedHubSpotUserAvatar, {
        size: "xs",
        hubSpotUserEmail: email || ''
      }), /*#__PURE__*/_jsx(Text, {
        marginInlineStart: "200",
        children: displayName
      })]
    })
  });
  if (isMemberOfSelectedTeam) {
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: "left",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ui-asset-management-lib.tooltips.userTooltip"
      }),
      children: checkbox
    });
  }
  if (disabled && disallowedUserTooltip) {
    return /*#__PURE__*/_jsx(Tooltip, {
      placement: "left",
      title: /*#__PURE__*/_jsx(FormattedMessage, {
        message: disallowedUserTooltip
      }),
      children: checkbox
    });
  }
  return checkbox;
};
const SelectableUsersList = ({
  userIdsForSelectedTeamIds = [],
  selectedUserIds = [],
  onAddUser,
  onRemoveUser,
  allowedUserIds,
  disabledUsers,
  disallowedUserTooltip
}) => {
  const pageSize = 50;
  const {
    loadOptions
  } = usePaginatedUsers(pageSize);
  const [page, setPage] = useState(0);
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPage] = useState(0);
  const loadNewPage = useCallback(newPage => {
    if (loadOptions) {
      loadOptions('', (__error, data) => {
        if (data) {
          setUsers(data.options);
        }
        if (data !== null && data !== void 0 && data.pagination.total) {
          setTotalPage(Math.ceil(data.pagination.total / pageSize));
        }
      }, {
        offset: (newPage - 1) * pageSize
      });
    }
  }, [loadOptions]);
  useEffect(() => {
    if (page === 0) {
      // Handles initial page load
      setPage(1);
    } else {
      loadNewPage(page);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);
  const renderUserCheckbox = user => {
    const {
      id
    } = user;
    const isMemberOfSelectedTeam = userIdsForSelectedTeamIds.includes(id);
    const checked = selectedUserIds.includes(id) || isMemberOfSelectedTeam;
    const displayName = formatUserFullNameForDisplay(user);
    let isDisabled = false;
    if (disabledUsers) {
      isDisabled = disabledUsers.includes(user.id);
    } else if (allowedUserIds) {
      isDisabled = !allowedUserIds.includes(user.id);
    }
    return /*#__PURE__*/_jsx(UserCheckbox, {
      id: id,
      displayName: displayName,
      isMemberOfSelectedTeam: isMemberOfSelectedTeam,
      checked: checked,
      disabled: isDisabled,
      onAddUser: onAddUser,
      onRemoveUser: onRemoveUser,
      disallowedUserTooltip: disallowedUserTooltip
    }, `${id}-user-checkbox`);
  };
  return /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsx(List, {
      children: users.map(renderUserCheckbox)
    }), /*#__PURE__*/_jsx(Paginator, {
      paddingBlock: "500",
      pageCount: totalPages,
      page: page,
      onPageChange: ({
        target: {
          value: newPage
        }
      }) => {
        setPage(newPage);
      }
    })]
  });
};
export default SelectableUsersList;